import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { formatDayTime } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Giving a customer their portal link. §6.2 `CUS-007`.
 *
 * **The shop delivers it.** WhatsApp and SMS are out of v1 and `MSG-001a` says shop customers
 * largely do not use email, so the only channel built is this: the link is shown once, with a copy
 * button, and the shopkeeper pastes it into whatever the customer actually reads. Emailing it would
 * add a message kind that the prescription opt-in triggers gate, which is a decision of its own.
 *
 * **There is no default lifetime.** The spec names none, so the field starts empty and the button
 * refuses until a whole number of hours is typed. A default here would be the shop's policy chosen
 * by whoever wrote the screen.
 *
 * **The link is built from the answer, not stored.** The server keeps a hash and cannot show the
 * token again. It rides in the URL fragment, which a browser never sends to a server or a log, and
 * the public page reads it from there. (The write queue keeps `sent` writes for the idempotency
 * window, so the answer is also in this device's queue storage for that long, the same as every
 * other answer it carries.)
 *
 * **The clipboard and the origin are injected**, for the reason `download.ts` records: neither can
 * be observed in jsdom, and a screen calling `navigator.clipboard` directly has an untestable
 * branch at the end of its main flow.
 */

export interface CustomerPortalLinkProps {
  readonly customerId: string
  readonly customerName: string
  /** Appendix B's `Prescription - print, share`, the row the server checks. */
  readonly canShare: boolean
  readonly timezone: string
  /** `window.location.origin`, so the link points at whichever host the shop is using. */
  readonly origin: string
  /** Resolves when the text is on the clipboard and rejects when the browser refuses. */
  readonly copyText: (text: string) => Promise<void>
}

interface Issued {
  readonly url: string
  readonly expiresAt: string
  readonly includesPrescriptions: boolean
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}

/** The answer of `POST /customers/:id/portal-link`, mapped rather than cast. Null if it is short. */
function toIssued(response: unknown, origin: string): Issued | null {
  const body = asRecord(response)
  const link = asRecord(body['link'])
  const token = body['token']
  const tenantId = body['tenantId']
  const expiresAt = link['expiresAt']
  if (
    typeof token !== 'string' ||
    typeof tenantId !== 'string' ||
    typeof expiresAt !== 'string' ||
    typeof link['includesPrescriptions'] !== 'boolean'
  ) {
    return null
  }
  return {
    url: `${origin}/portal/${encodeURIComponent(tenantId)}#${token}`,
    expiresAt,
    includesPrescriptions: link['includesPrescriptions'],
  }
}

export function CustomerPortalLink({
  customerId,
  customerName,
  canShare,
  timezone,
  origin,
  copyText,
}: CustomerPortalLinkProps): JSX.Element | null {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [hours, setHours] = useState('')
  const [issued, setIssued] = useState<Issued | null>(null)
  const [revoked, setRevoked] = useState<number | null>(null)
  const [copy, setCopy] = useState<'idle' | 'done' | 'failed'>('idle')
  const [writeId, setWriteId] = useState<string | null>(null)
  const [kind, setKind] = useState<'issue' | 'revoke'>('issue')
  const [error, setError] = useState<string | null>(null)

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    if (kind === 'issue') {
      const link = toIssued(write.response, origin)
      if (link === null) {
        // The server took the write and answered with something this build cannot read. Said
        // plainly, because a silent nothing would send the shopkeeper round again and make a
        // second link.
        setError(t('portal.failed'))
        return
      }
      setIssued(link)
      setRevoked(null)
      setCopy('idle')
      setHours('')
      return
    }
    const count = asRecord(write.response)['revoked']
    setRevoked(typeof count === 'number' ? count : 0)
    setIssued(null)
  })

  const make = useCallback(async () => {
    // A whole number of hours, one or more: the server refuses anything else, and a refusal stops
    // the write queue, so this is checked before it is queued.
    if (!/^[0-9]+$/.test(hours.trim()) || Number(hours) < 1) {
      setError(t('portalLink.lifetimeRequired'))
      return
    }
    setError(null)
    setKind('issue')
    const write = (await enqueue({
      method: 'POST',
      path: `/customers/${encodeURIComponent(customerId)}/portal-link`,
      label: t('portalLink.queueLabel', { name: customerName }),
      body: { lifetimeHours: Number(hours) },
    })) as { id: string }
    setWriteId(write.id)
  }, [customerId, customerName, enqueue, hours, t])

  const revoke = useCallback(async () => {
    setError(null)
    setKind('revoke')
    const write = (await enqueue({
      method: 'DELETE',
      path: `/customers/${encodeURIComponent(customerId)}/portal-link`,
      label: t('portalLink.revokeQueueLabel', { name: customerName }),
      body: {},
    })) as { id: string }
    setWriteId(write.id)
  }, [customerId, customerName, enqueue, t])

  if (!canShare) return null

  const line = statusLine(t, snapshot, writeId, t('queue.subject.portalLink'))

  return (
    <section className="lm-portal-link" aria-labelledby="portal-link-heading">
      <h2 id="portal-link-heading" className="lm-portal-link__heading">
        {t('portalLink.heading')}
      </h2>
      <p className="lm-portal-link__effects">{t('portalLink.whatItIs')}</p>

      {issued === null ? null : (
        <div role="status">
          <p className="lm-portal-link__effects">{t('portalLink.ready')}</p>
          <label className="lm-field">
            <span className="lm-field__label">{t('portalLink.linkLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              readOnly
              value={issued.url}
              onFocus={(e) => {
                e.currentTarget.select()
              }}
            />
          </label>
          <button
            type="button"
            className="lm-button"
            onClick={() => {
              copyText(issued.url).then(
                () => {
                  setCopy('done')
                },
                () => {
                  setCopy('failed')
                },
              )
            }}
          >
            {t('portalLink.copy')}
          </button>
          {copy === 'done' ? (
            <p className="lm-portal-link__effects">{t('portalLink.copied')}</p>
          ) : null}
          {copy === 'failed' ? <p className="lm-error">{t('portalLink.copyFailed')}</p> : null}
          <p className="lm-portal-link__effects">
            {t('portalLink.expires', {
              when: formatDayTime(issued.expiresAt, timezone, locale),
            })}
          </p>
          <p className="lm-portal-link__effects">
            {issued.includesPrescriptions
              ? t('portalLink.withPrescriptions')
              : t('portalLink.withoutPrescriptions')}
          </p>
        </div>
      )}

      {revoked === null ? null : (
        <p className="lm-portal-link__effects" role="status">
          {t('portalLink.revoked', { count: revoked })}
        </p>
      )}

      {error === null ? null : <p className="lm-error">{error}</p>}
      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      <label className="lm-field">
        <span className="lm-field__label">{t('portalLink.lifetimeLabel')}</span>
        <Ltr>
          <input
            className="lm-input"
            inputMode="numeric"
            autoComplete="off"
            value={hours}
            onChange={(e) => {
              setHours(e.currentTarget.value)
            }}
          />
        </Ltr>
      </label>
      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void make()
        }}
      >
        {t('portalLink.make')}
      </button>
      <button
        type="button"
        className="lm-button lm-button--secondary"
        disabled={writeId !== null}
        onClick={() => {
          void revoke()
        }}
      >
        {t('portalLink.revoke')}
      </button>
    </section>
  )
}
